<script lang="ts" setup>
export type ArticleBoxType = 'primary' | 'secondary'
export type ArticleBoxTextColorType = 'white' | 'black'

export interface ArticleBoxProps {
    data: BlogPost
    linkType: BlogPostType
    type?: ArticleBoxType
    color?: ArticleBoxTextColorType
    altSwitch?: boolean
    headingLevel?: HeadingLevel
}

const props = withDefaults(defineProps<ArticleBoxProps>(), {
    linkType: 'magazine',
    type: 'primary',
    color: 'black',
    altSwitch: true,
    headingLevel: 'h3',
})

const router = useRouter()

const boxPaddings = computed<string>((): string => {
    if (props.type === 'primary') return 'px-4 pb-4 pt-3 pt-lg-5 pb-lg-6'
    if (props.type === 'secondary') return 'pt-4'

    return ''
})

const target = computed((): '_blank' | '_self' => {
    if (Number(props.data.targetBlank) === 1) return '_blank'
    return '_self'
})

const handleClick = (): void => {
    router.push(useBlogPostNamedRoute({
        slug: props.data.slug,
        type: props.linkType,
    }))
}
</script>

<template>
  <article
    class="h-100 position-relative overflow-hidden transition cursor-pointer"
    :class="{ 'rounded bg-white': props.type === 'primary' }"
    @click="handleClick"
  >
    <ArticleBoxThumbnail
      :data="props.data"
      :type="props.type"
      :alt-switch="props.altSwitch"
    />
    <Box
      class="mt-0"
      :padding="boxPaddings"
      :classes="props.type === 'secondary' ? '' : 'rounded'"
      decor-position="top-right"
      :decor="props.type === 'primary'"
      :color="props.type === 'secondary' ? 'transparent' : 'white'"
    >
      <div class="d-flex align-items-center justify-content-between">
        <Label
          v-if="props.data.category?.title"
          :label="props.data.category.title"
          background-color="var(--global-color-secondary)"
          text-color="var(--global-color-white)"
          class="d-inline-flex"
        />
        <ArticleDate
          v-if="props.data.published_at"
          :date="props.data.published_at"
          :type="props.type"
          :class="`text-${props.color}`"
        />
      </div>
      <Heading
        :level="props.headingLevel"
        class="mt-2"
        :class="{
          zeta: type === 'primary',
          'mt-lg-3':
            props.data.category?.title?.length > 0 &&
            props.type === 'secondary',
          epsilon: type === 'secondary',
          'fw-bolder': type === 'secondary',
        }"
      >
        <VNuxtLink
          :class="`text-decoration-underline ${props.color === 'white' ? 'text-white' : 'text-black'}`"
          :to="
            useBlogPostNamedRoute({
              slug: props.data.slug,
              type: props.linkType,
            })
          "
          :label="props.data.title"
          :target="target"
        >
          {{ props.data.title }}
        </VNuxtLink>
      </Heading>
    </Box>
  </article>
</template>

<style scoped lang="scss">
@import './ArticleBox.scss';
</style>
